/**
 * The receiving report — one S&S shipment at the receiving table, and the actions on it. Two modes:
 *
 *  - BOX receiving (the norm — S&S lists every box's contents): a scanned carton opens THAT box's lines,
 *    prefilled with its share; a packing slip opens the invoice with its lines grouped by box. Each
 *    receipt is one box (`POST /receiving/confirm-boxes`) and is undone per box
 *    (`POST /receiving/reverse-box`). A box still with the carrier is shown, greyed, and is never
 *    claimed from S&S — the invoice simply stays "1 of 2 boxes received".
 *  - WHOLE-invoice receiving (no usable per-box manifest, or an invoice already received as a whole):
 *    the original flow — every line at once, the "not every box delivered" gate.
 *
 * The lookup arrives ledger-joined (`receipt`, per-line `receivedQty`, per-box `receipt`), so an
 * already-received invoice or box says so BEFORE anyone presses a button.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Package,
  PackageCheck,
  ScanLine,
  Truck,
  Undo2,
} from 'lucide-react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { BlankDetailDialog } from './blank-detail-dialog';
import { blankLabel, invalidateInventory, variantLabel } from './inventory-lib';

export type ReceivingResult = components['schemas']['ReceivingLookupResult'];
type ReceivingLine = ReceivingResult['lines'][number];
type ReceivingBox = NonNullable<ReceivingResult['boxes']>[number];
type DeliveryState = NonNullable<ReceivingResult['deliveryState']>;
type ReceiptState = NonNullable<ReceivingResult['receipt']>;
type BlankOnHand = components['schemas']['BlankOnHand'];
// Nested schemas aren't named components — derive from their parents.
type LineOverride = NonNullable<
  components['schemas']['ReceivingConfirmRequest']['lineOverrides']
>[number];
type ConfirmBoxesResult = components['schemas']['ReceivingConfirmBoxesResult'];
type ReverseBoxResult = components['schemas']['ReceivingReverseBoxResult'];

/** react-query key for the open-shortages claim worklist — invalidated after every receipt/undo. */
export const SHORTAGES_KEY = ['receiving', 'shortages'] as const;

export function fmtDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "Mon 12:14" inside the last week, "Sep 14, 12:14" beyond it — what "received when" needs. */
export function fmtWhen(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const recent = Date.now() - d.getTime() < 6 * 86_400_000;
  const day = recent
    ? d.toLocaleDateString(undefined, { weekday: 'short' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${day} ${time}`;
}

/** Blanks in the shipment, said once: "Tultex 602 Black + 2 more". */
function brandSummary(lines: readonly ReceivingLine[]): string {
  const names = [
    ...new Set(
      lines
        .map((l) => [l.brandName, l.styleName, l.colorName].filter(Boolean).join(' '))
        .filter(Boolean),
    ),
  ];
  if (names.length === 0) return '';
  return names.length === 1 ? (names[0] ?? '') : `${names[0]} + ${names.length - 1} more`;
}

/**
 * Box receiving needs every box's contents and every box line tied to exactly one invoice line. An
 * order that fails either (no manifest, "contents not listed", a SKU on two lines) is received as a
 * whole — the server refuses per-box writes for it anyway.
 */
function manifestUsable(result: ReceivingResult): boolean {
  const boxes = result.boxes ?? [];
  return (
    boxes.length > 0 &&
    boxes.every((b) => b.contentsKnown && b.lines.every((l) => !l.sku || l.lineNumber != null))
  );
}

/** Where one box stands: in our ledger first (received / undone), else per S&S (here / still out). */
type BoxState = 'received' | 'undone' | 'here' | 'out';
function boxStateOf(b: ReceivingBox, hereOverride: boolean): BoxState {
  if (b.receipt && !b.receipt.reversed) return 'received';
  if (b.receipt?.reversed) return 'undone';
  if (b.deliveryState === 'in_transit' && !hereOverride) return 'out';
  return 'here';
}
const keyOf = (box: number, line: number) => `${box}:${line}`;

/** "box 2 is" · "boxes 2 and 5 are" · "boxes 2, 5 and 7 are" · past three "6 of the 8 boxes are". */
function boxesAre(nums: number[], total: number): string {
  if (nums.length === total && total > 1) return `all ${total} boxes are`;
  if (nums.length > 3) return `${nums.length} of the ${total} boxes are`;
  if (nums.length === 1) return `box ${nums[0]} is`;
  return `boxes ${nums.slice(0, -1).join(', ')} and ${nums[nums.length - 1]} are`;
}

/**
 * Which mode this shipment is received in. Box mode whenever the manifest allows it and the invoice
 * was not already received as a whole (a whole receipt can only be undone as a whole).
 */
export function ReceivingReport({
  result,
  onScanNext,
  onReceived,
}: {
  result: ReceivingResult;
  /** "Scan next box" — clear the report and put the cursor back in the scan field. */
  onScanNext: () => void;
  /** A receipt just landed — re-arm the scanner (the report stays on screen). */
  onReceived?: () => void;
}) {
  if (!manifestUsable(result) || result.receipt?.wholeInvoice) {
    return <WholeShipment result={result} />;
  }
  return <BoxShipment result={result} onScanNext={onScanNext} onReceived={onReceived} />;
}

/** Header pills for the invoice's receipt state — the first thing a second worker must see. */
function ReceiptPill({ receipt }: { receipt: ReceiptState | undefined }) {
  if (!receipt || receipt.status === 'none') return null;
  if (receipt.status === 'complete') {
    return (
      <Badge
        variant="outline"
        className="border-emerald-300 text-emerald-700 dark:text-emerald-400"
      >
        <CheckCircle2 className="size-3" /> Received {fmtWhen(receipt.lastReceivedAt) ?? ''}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">
      {receipt.boxesReceived}
      {receipt.boxesTotal != null ? ` of ${receipt.boxesTotal}` : ''} boxes received
    </Badge>
  );
}

function ShipmentHeader({
  result,
  title,
  receipt,
  children,
}: {
  result: ReceivingResult;
  title: string;
  receipt: ReceiptState | undefined;
  children?: React.ReactNode;
}) {
  const brands = brandSummary(result.lines);
  return (
    <div className="space-y-2 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        {children}
        <ReceiptPill receipt={receipt} />
        {result.deliveryState && receipt?.status !== 'complete' && (
          <DeliveryBadge state={result.deliveryState} />
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-muted-foreground">
        {!title.startsWith('Invoice') && <span>Invoice {result.invoiceNumber}</span>}
        {result.poNumber && (
          <span>
            PO <span className="font-medium text-foreground">{result.poNumber}</span>
          </span>
        )}
        {result.orderNumber && <span>order {result.orderNumber}</span>}
        {brands && <span>{brands}</span>}
        <span className="tabular-nums">
          {result.totals.totalShipped} pieces · {result.totals.lineCount} lines
          {result.totalBoxes != null && ` · ${result.totalBoxes} boxes`}
        </span>
        {result.shippingCarrier && (
          <span>
            {result.shippingCarrier}
            {result.warehouseAbbr && ` · from ${result.warehouseAbbr}`}
          </span>
        )}
      </div>
    </div>
  );
}

const TILE =
  'flex w-full flex-col gap-0.5 rounded-lg border p-2.5 text-left text-sm transition-colors aria-pressed:border-foreground aria-pressed:ring-1 aria-pressed:ring-foreground';

/**
 * A tile's slot in the strip. On a phone the strip is ONE row that scrolls sideways — about two
 * tiles and the edge of a third in view (an 8-box invoice used to stack nine full-width tiles,
 * ~1,000px before the first line); from `sm` up it is the two/three-column grid.
 */
const SLOT = 'w-[44%] shrink-0 snap-start sm:w-auto sm:shrink';

/** One tile per box — tap to focus its lines. State comes from our ledger first, then S&S. */
function BoxStrip({
  boxes,
  focus,
  stateOf,
  onFocus,
}: {
  boxes: ReceivingBox[];
  focus: number | 'all';
  stateOf: (b: ReceivingBox) => BoxState;
  onFocus: (f: number | 'all') => void;
}) {
  const totalPieces = boxes.reduce((s, b) => s + b.pieces, 0);
  const stripRef = useRef<HTMLFieldSetElement>(null);
  // When the strip scrolls (phone), start it on the focused tile; a no-op on the grid.
  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const slot = strip.querySelector<HTMLElement>(`[data-box="${focus}"]`);
    if (slot) strip.scrollLeft = slot.offsetLeft;
  }, [focus]);
  return (
    <fieldset
      ref={stripRef}
      className="relative m-0 flex min-w-0 snap-x gap-2 overflow-x-auto border-0 p-0 pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 lg:grid-cols-3"
      aria-label="Boxes in this shipment"
    >
      {boxes.length > 1 && (
        <button
          type="button"
          aria-pressed={focus === 'all'}
          data-box="all"
          onClick={() => onFocus('all')}
          className={cn(TILE, SLOT, 'bg-muted/30')}
        >
          <span className="font-medium">All boxes</span>
          <span className="text-xs text-muted-foreground">
            {boxes.length} boxes · {totalPieces} pieces
          </span>
        </button>
      )}
      {boxes.map((b) => {
        const st = stateOf(b);
        return (
          <div
            key={b.boxNumber}
            data-box={b.boxNumber}
            className={cn(
              'rounded-lg',
              SLOT,
              st === 'received' && 'bg-emerald-50/70 dark:bg-emerald-950/20',
              st === 'out' && 'bg-amber-50/70 dark:bg-amber-950/20',
              st === 'undone' && 'bg-muted/40',
            )}
          >
            <button
              type="button"
              aria-pressed={focus === b.boxNumber}
              onClick={() => onFocus(b.boxNumber)}
              className={cn(
                TILE,
                'bg-transparent',
                st === 'received' && 'border-emerald-300',
                st === 'out' && 'border-amber-300',
              )}
            >
              <span className="flex items-center justify-between gap-2 font-medium">
                <span>
                  Box {b.boxNumber}
                  {b.scanned && (
                    <Badge variant="outline" className="ml-2 border-foreground text-foreground">
                      <ScanLine className="size-3" /> Scanned
                    </Badge>
                  )}
                </span>
                {st === 'received' && (
                  <CheckCircle2 className="size-4 text-emerald-700" aria-hidden />
                )}
                {st === 'out' && <Truck className="size-4 text-amber-700" aria-hidden />}
              </span>
              <span
                className={cn(
                  'text-xs',
                  st === 'received' && 'text-emerald-700 dark:text-emerald-400',
                  st === 'out' && 'text-amber-700 dark:text-amber-400',
                  st === 'here' && 'text-muted-foreground',
                  st === 'undone' && 'text-muted-foreground',
                )}
              >
                {st === 'received' && b.receipt
                  ? `Received ${fmtWhen(b.receipt.receivedAt) ?? ''}${b.receipt.receivedBy ? ` · ${b.receipt.receivedBy}` : ''}`
                  : st === 'received'
                    ? 'Received just now'
                    : st === 'out'
                      ? `With UPS · ${statusText(b.deliveryStatus, b.deliveryState)}`
                      : st === 'undone'
                        ? 'Receipt undone'
                        : statusText(b.deliveryStatus, b.deliveryState)}
              </span>
              <span className="text-xs text-muted-foreground">
                {b.pieces} {b.pieces === 1 ? 'piece' : 'pieces'} · {b.lines.length}{' '}
                {b.lines.length === 1 ? 'line' : 'lines'}
                {b.weightLb != null && ` · ${b.weightLb} lb`}
              </span>
            </button>
            {b.trackingNumber && (
              <div className="px-2.5 pb-2">
                <TrackingLink box={b} />
              </div>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}

/** Post-receipt / post-undo on-hand, each blank a drill-in to its drawer. */
function OnHandList({
  rows,
  label,
  onPick,
}: {
  rows: BlankOnHand[];
  label: string;
  onPick: (b: BlankOnHand) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <ul className="divide-y rounded-md border bg-background text-sm">
        {rows.map((b) => (
          <li key={b.ssSku}>
            <button
              type="button"
              onClick={() => onPick(b)}
              className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left hover:bg-muted/50"
            >
              <span className="min-w-0 truncate">
                <span className="text-primary underline-offset-2 hover:underline">
                  {blankLabel(b)}
                </span>
                {variantLabel(b) && (
                  <span className="text-muted-foreground"> · {variantLabel(b)}</span>
                )}
              </span>
              <span
                className={cn(
                  'shrink-0 tabular-nums',
                  b.onHand < 0 && 'text-red-700 dark:text-red-400',
                )}
              >
                {b.onHand}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Box receiving. `focus` is the box whose lines are on screen (the scanned carton by default) or
 * 'all' (the invoice view, lines grouped by box). The Received counts live per (box, line), so a split
 * SKU is counted where it was packed. Only boxes that are HERE (per our ledger + S&S, or marked here by
 * the operator) are receivable; a box still with the carrier is greyed and never sent.
 */
function BoxShipment({
  result,
  onScanNext,
  onReceived,
}: {
  result: ReceivingResult;
  onScanNext: () => void;
  onReceived?: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  // Cosmetic gate mirroring the server's requireInventoryWrite; the server 403 is the real one.
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const boxes = result.boxes ?? [];
  const lineByNumber = useMemo(() => {
    const m = new Map<number, ReceivingLine>();
    for (const l of result.lines) if (l.lineNumber != null) m.set(l.lineNumber, l);
    return m;
  }, [result.lines]);
  const scanned = boxes.find((b) => b.scanned);
  const [focus, setFocus] = useState<number | 'all'>(scanned ? scanned.boxNumber : 'all');
  // Boxes S&S still shows in transit that the operator says are physically here (S&S lags UPS both ways).
  const [hereOverrides, setHereOverrides] = useState<ReadonlySet<number>>(new Set());
  // Operator-entered actual-arrived per (box, line). Absent = the box's manifest share.
  const [received, setReceived] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [done, setDone] = useState<ConfirmBoxesResult | null>(null);
  const [undone, setUndone] = useState<Record<number, ReverseBoxResult>>({});
  const [askUndo, setAskUndo] = useState<number | null>(null);
  const [selected, setSelected] = useState<BlankOnHand | null>(null);

  const justReceived = useMemo(
    () =>
      new Set((done?.boxes ?? []).filter((o) => o.status === 'received').map((o) => o.boxNumber)),
    [done],
  );
  const stateOf = useCallback(
    (b: ReceivingBox): BoxState => {
      if (undone[b.boxNumber]?.status === 'reversed') return 'undone';
      if (justReceived.has(b.boxNumber)) return 'received';
      return boxStateOf(b, hereOverrides.has(b.boxNumber));
    },
    [undone, justReceived, hereOverrides],
  );

  const focused = focus === 'all' ? boxes : boxes.filter((b) => b.boxNumber === focus);
  const targets = focused.filter((b) => stateOf(b) === 'here');
  const qtyOf = useCallback(
    (b: ReceivingBox, l: ReceivingBox['lines'][number]) =>
      l.lineNumber == null ? l.qty : (received[keyOf(b.boxNumber, l.lineNumber)] ?? l.qty),
    [received],
  );
  const setQty = useCallback((box: number, line: number, raw: string, share: number) => {
    const parsed = raw === '' ? 0 : Math.trunc(Number(raw));
    const clamped = Number.isFinite(parsed) ? Math.min(Math.max(0, parsed), share) : 0;
    setReceived((prev) => ({ ...prev, [keyOf(box, line)]: clamped }));
  }, []);
  const targetPieces = targets.reduce(
    (s, b) => s + b.lines.reduce((t, l) => t + qtyOf(b, l), 0),
    0,
  );
  const short = targets.reduce(
    (acc, b) => {
      for (const l of b.lines) {
        const missing = l.qty - qtyOf(b, l);
        if (missing > 0) {
          acc.lines += 1;
          acc.pieces += missing;
        }
      }
      return acc;
    },
    { lines: 0, pieces: 0 },
  );
  const stillOut = boxes.filter((b) => stateOf(b) === 'out');
  const completes = boxes.every((b) => targets.includes(b) || stateOf(b) === 'received');
  const trimmedNote = note.trim();

  const confirm = useMutation({
    mutationFn: async (): Promise<ConfirmBoxesResult> => {
      const body = {
        code: result.scannedCode,
        boxes: targets.map((b) => {
          const overrides: LineOverride[] = [];
          for (const l of b.lines) {
            if (l.lineNumber == null) continue;
            const q = qtyOf(b, l);
            if (q !== l.qty) {
              overrides.push({
                lineNumber: l.lineNumber,
                receivedQty: q,
                note: trimmedNote || undefined,
              });
            }
          }
          return {
            boxNumber: b.boxNumber,
            lineOverrides: overrides.length ? overrides : undefined,
          };
        }),
      };
      const { data, error } = await client.POST('/receiving/confirm-boxes', { body });
      if (error) throw new Error(error?.error?.detail ?? 'Receive failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: (data) => {
      setDone(data);
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
      onReceived?.();
    },
  });

  const reverseBox = useMutation({
    mutationFn: async (boxNumber: number): Promise<ReverseBoxResult> => {
      const { data, error } = await client.POST('/receiving/reverse-box', {
        body: { code: result.scannedCode, boxNumber },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Undo failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: (data) => {
      setUndone((prev) => ({ ...prev, [data.boxNumber]: data }));
      setAskUndo(null);
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
    },
  });

  // The invoice's receipt state as we best know it now: the latest write's answer, else the lookup's.
  const undoneList = Object.values(undone);
  const latestUndo = undoneList[undoneList.length - 1];
  const receipt = latestUndo?.receipt ?? done?.receipt ?? result.receipt;
  const onHandNow = latestUndo?.onHand ?? done?.onHand ?? [];

  const focusedBox = focus === 'all' ? undefined : boxes.find((b) => b.boxNumber === focus);
  // The out-box alert speaks about what is on screen: the focused box alone in a single-box view,
  // every box still with the carrier in the invoice view (the strip shows the rest either way).
  const outShown = focusedBox
    ? stillOut.filter((b) => b.boxNumber === focusedBox.boxNumber)
    : stillOut;
  const outElsewhere = stillOut.filter((b) => !outShown.includes(b));
  const title = focusedBox
    ? `Box ${focusedBox.boxNumber} of ${boxes.length}`
    : `Invoice ${result.invoiceNumber}`;
  const undoBlock = (boxNumber: number) => {
    const b = boxes.find((x) => x.boxNumber === boxNumber);
    const pieces =
      done?.boxes.find((o) => o.boxNumber === boxNumber)?.receivedPieces ??
      b?.receipt?.pieces ??
      b?.pieces ??
      0;
    return askUndo === boxNumber ? (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
        <span className="text-amber-800 dark:text-amber-300">
          Undo box {boxNumber}? Its {pieces} {pieces === 1 ? 'piece comes' : 'pieces come'} back out
          of stock and its claims close. You won’t be able to re-scan it — if the box is really
          here, correct the line counts instead.
        </span>
        <div className="ml-auto flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAskUndo(null)}
            disabled={reverseBox.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => reverseBox.mutate(boxNumber)}
            disabled={reverseBox.isPending}
          >
            {reverseBox.isPending && <Loader2 className="size-4 animate-spin" />}
            Yes, undo box {boxNumber}
          </Button>
        </div>
      </div>
    ) : null;
  };

  return (
    <div className="space-y-4">
      <ShipmentHeader result={result} title={title} receipt={receipt}>
        {focusedBox?.scanned && (
          <Badge variant="outline" className="border-foreground text-foreground">
            <ScanLine className="size-3" /> Scanned
          </Badge>
        )}
      </ShipmentHeader>

      <BoxStrip boxes={boxes} focus={focus} stateOf={stateOf} onFocus={setFocus} />

      {done ? (
        <div className="space-y-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
          <h3 className="flex items-center gap-2 text-base font-semibold text-emerald-900 dark:text-emerald-200">
            <CheckCircle2 className="size-5 text-emerald-700" aria-hidden />
            {justReceived.size === 0
              ? done.boxes.some((o) => o.status === 'undone')
                ? 'This box was undone earlier — nothing added. Correct the line counts instead.'
                : 'Nothing new — already received'
              : justReceived.size === 1
                ? `Box ${[...justReceived][0]} received`
                : `${justReceived.size} boxes received`}
            {justReceived.size > 0 && done.receipt?.status === 'complete'
              ? ` — invoice ${result.invoiceNumber} complete`
              : ''}
          </h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-emerald-900 dark:text-emerald-200">
            <span>
              <strong>{done.receivedPieces}</strong>{' '}
              {done.receivedPieces === 1 ? 'piece' : 'pieces'} added now
            </span>
            {done.receipt && (
              <span>
                <strong>
                  {done.receipt.boxesReceived}
                  {done.receipt.boxesTotal != null ? ` of ${done.receipt.boxesTotal}` : ''}
                </strong>{' '}
                boxes received
              </span>
            )}
            {done.purchaseOrder && (
              <span>
                PO <span className="font-mono font-medium">{done.purchaseOrder.poNumber}</span>{' '}
                {done.purchaseOrder.status === 'CLOSED'
                  ? 'closed — received in full'
                  : 'partly received'}
              </span>
            )}
            {done.boxes.some((o) => o.shortages.length > 0) && (
              <span>
                Logged {done.boxes.reduce((n, o) => n + o.shortages.length, 0)} S&amp;S{' '}
                {done.boxes.reduce((n, o) => n + o.shortages.length, 0) === 1 ? 'claim' : 'claims'}{' '}
                — see below
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button className="h-11 w-full sm:h-9 sm:w-auto" onClick={onScanNext}>
              <ScanLine className="size-4" /> Scan next box
            </Button>
            {[...justReceived].map((n) => (
              <Button
                key={n}
                variant="outline"
                className="h-11 w-full sm:h-9 sm:w-auto"
                onClick={() => setAskUndo(n)}
                disabled={!canWrite || reverseBox.isPending || !!undone[n]}
              >
                <Undo2 className="size-4" /> Undo box {n}
              </Button>
            ))}
          </div>
          {askUndo != null && undoBlock(askUndo)}
          {reverseBox.isError && (
            <p className="text-sm text-destructive">
              {reverseBox.error instanceof Error ? reverseBox.error.message : 'Undo failed.'}
            </p>
          )}
          {undoneList.map((u) => (
            <p key={u.boxNumber} className="text-sm text-amber-800 dark:text-amber-300">
              Box {u.boxNumber} undone — {u.reversedPieces}{' '}
              {u.reversedPieces === 1 ? 'piece' : 'pieces'} removed from stock.
            </p>
          ))}
          <OnHandList
            rows={onHandNow}
            label={latestUndo ? 'On hand after the undo' : 'On hand after this receipt'}
            onPick={setSelected}
          />
        </div>
      ) : (
        <>
          <section
            className="space-y-3"
            aria-label={focusedBox ? `Lines in box ${focusedBox.boxNumber}` : 'Lines by box'}
          >
            {focused.map((b) => (
              <BoxGroup
                key={b.boxNumber}
                box={b}
                st={stateOf(b)}
                boxes={boxes}
                stateOf={stateOf}
                lineByNumber={lineByNumber}
                qtyOf={qtyOf}
                setQty={setQty}
                showHeader={focus === 'all'}
                onHere={(n) => setHereOverrides((prev) => new Set([...prev, n]))}
              />
            ))}
          </section>

          {targets.length > 0 ? (
            <div className="space-y-3 border-t pt-3">
              {short.lines > 0 && (
                <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/40">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    {short.pieces} {short.pieces === 1 ? 'piece' : 'pieces'} missing from{' '}
                    {targets.length === 1 ? `box ${targets[0]?.boxNumber}` : 'these boxes'} across{' '}
                    {short.lines} {short.lines === 1 ? 'line' : 'lines'}. Only what arrived goes
                    into stock — the rest becomes an S&amp;S claim for that box.
                  </p>
                  <div className="space-y-1">
                    <Label
                      htmlFor="box-shortage-note"
                      className="text-xs text-amber-800 dark:text-amber-300"
                    >
                      Note for the S&amp;S claim (optional)
                    </Label>
                    <Input
                      id="box-shortage-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={280}
                      placeholder="e.g. 2XL not in the box — S&S packing error"
                      className="bg-background"
                    />
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  className="h-11 w-full sm:h-10 sm:w-auto"
                  onClick={() => confirm.mutate()}
                  disabled={!canWrite || confirm.isPending}
                  title={canWrite ? undefined : 'Admins only'}
                >
                  {confirm.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <PackageCheck className="size-4" />
                  )}
                  {targets.length === 1
                    ? `Receive box ${targets[0]?.boxNumber}`
                    : `Receive ${targets.length} boxes`}{' '}
                  · {targetPieces} {targetPieces === 1 ? 'piece' : 'pieces'}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {!canWrite
                    ? 'Read-only — ask an admin to record the receipt.'
                    : completes
                      ? `Completes invoice ${result.invoiceNumber}${result.poNumber ? ` and closes PO ${result.poNumber}` : ''}.`
                      : `${boxes.length - targets.length - boxes.filter((b) => stateOf(b) === 'received').length} ${boxes.length - targets.length - boxes.filter((b) => stateOf(b) === 'received').length === 1 ? 'box' : 'boxes'} still to come after this.`}
                </span>
              </div>
              {confirm.isError && (
                <p className="text-sm text-destructive">
                  {confirm.error instanceof Error ? confirm.error.message : 'Receive failed.'}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 border-t pt-3">
              {focusedBox && stateOf(focusedBox) === 'received' && (
                <Alert className="border-emerald-300 text-emerald-800 dark:text-emerald-300">
                  <CheckCircle2 className="size-4" />
                  <AlertTitle>Box {focusedBox.boxNumber} is already in</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      Received {fmtWhen(focusedBox.receipt?.receivedAt) ?? 'earlier'}
                      {focusedBox.receipt?.receivedBy ? ` by ${focusedBox.receipt.receivedBy}` : ''}
                      {focusedBox.receipt ? ` — ${focusedBox.receipt.pieces} pieces` : ''}.
                    </p>
                    {canWrite && !undone[focusedBox.boxNumber] && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAskUndo(focusedBox.boxNumber)}
                      >
                        <Undo2 className="size-4" /> Undo box {focusedBox.boxNumber}
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              {!focusedBox &&
                stillOut.length === 0 &&
                boxes.every((b) => stateOf(b) !== 'here') && (
                  <Alert className="border-emerald-300 text-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="size-4" />
                    <AlertTitle>Every box of this invoice has been received</AlertTitle>
                    <AlertDescription>
                      Tap a box above to see its receipt or undo it.
                    </AlertDescription>
                  </Alert>
                )}
              {outShown.length > 0 && (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  <Truck className="size-4" />
                  <AlertTitle>
                    Nothing to receive right now —{' '}
                    {boxesAre(
                      outShown.map((b) => b.boxNumber),
                      boxes.length,
                    )}{' '}
                    still with UPS
                  </AlertTitle>
                  <AlertDescription className="space-y-2 text-amber-900 dark:text-amber-200">
                    <p>
                      No S&amp;S claim is opened for a box that is still with the carrier — this
                      invoice stays on the waiting list until it is scanned.
                      {outElsewhere.length > 0 &&
                        ` ${boxesAre(
                          outElsewhere.map((b) => b.boxNumber),
                          boxes.length,
                        ).replace(/^./, (c) => c.toUpperCase())} also still with UPS.`}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {outShown.map((b) => (
                        <Button
                          key={b.boxNumber}
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setHereOverrides((prev) => new Set([...prev, b.boxNumber]))
                          }
                        >
                          Box {b.boxNumber} is actually here
                        </Button>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              {askUndo != null && undoBlock(askUndo)}
              {reverseBox.isError && (
                <p className="text-sm text-destructive">
                  {reverseBox.error instanceof Error ? reverseBox.error.message : 'Undo failed.'}
                </p>
              )}
              {undoneList.map((u) => (
                <p key={u.boxNumber} className="text-sm text-amber-800 dark:text-amber-300">
                  Box {u.boxNumber} undone — {u.reversedPieces}{' '}
                  {u.reversedPieces === 1 ? 'piece' : 'pieces'} removed from stock.
                </p>
              ))}
              {latestUndo && (
                <OnHandList
                  rows={latestUndo.onHand}
                  label="On hand after the undo"
                  onPick={setSelected}
                />
              )}
            </div>
          )}
        </>
      )}

      <BlankDetailDialog blank={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

const BOX_GRID =
  'md:grid md:grid-cols-[2.5rem_minmax(0,1fr)_5rem_7rem_5rem] md:items-center md:gap-x-2';

/** One box's lines: what S&S packed in it, and (when the box is here) how many actually came out. */
function BoxGroup({
  box,
  st,
  boxes,
  stateOf,
  lineByNumber,
  qtyOf,
  setQty,
  showHeader,
  onHere,
}: {
  box: ReceivingBox;
  st: BoxState;
  boxes: ReceivingBox[];
  stateOf: (b: ReceivingBox) => BoxState;
  lineByNumber: Map<number, ReceivingLine>;
  qtyOf: (b: ReceivingBox, l: ReceivingBox['lines'][number]) => number;
  setQty: (box: number, line: number, raw: string, share: number) => void;
  showHeader: boolean;
  /** S&S still shows the box in transit but it is physically here — make it receivable. */
  onHere: (box: number) => void;
}) {
  const stateLabel =
    st === 'received'
      ? `received ${fmtWhen(box.receipt?.receivedAt) ?? ''}`.trim()
      : st === 'out'
        ? 'with UPS · not receivable yet'
        : st === 'undone'
          ? 'receipt undone'
          : 'here';
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border',
        st === 'out' && 'border-amber-300',
        st === 'received' && 'border-emerald-300',
      )}
    >
      {showHeader && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-xs font-medium',
            st === 'out'
              ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
              : 'bg-muted/40 text-muted-foreground',
          )}
        >
          {st === 'received' ? (
            <CheckCircle2 className="size-3.5 text-emerald-700" aria-hidden />
          ) : st === 'out' ? (
            <Truck className="size-3.5" aria-hidden />
          ) : (
            <Package className="size-3.5" aria-hidden />
          )}
          <span className="text-foreground">Box {box.boxNumber}</span>
          <span>· {stateLabel}</span>
          <span className="ml-auto tabular-nums">{box.pieces} pieces</span>
          {st === 'out' && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="ml-2 border-amber-300 bg-background"
              onClick={() => onHere(box.boxNumber)}
            >
              Box {box.boxNumber} is actually here
            </Button>
          )}
        </div>
      )}
      <div
        className={cn(
          BOX_GRID,
          'hidden border-b bg-muted/20 px-3 py-1.5 text-xs font-medium text-muted-foreground md:grid',
          showHeader && 'border-t',
        )}
      >
        <span>#</span>
        <span>Item</span>
        <span className="text-right">In box</span>
        <span className="text-right">Received</span>
        <span />
      </div>
      <ul>
        {box.lines.map((bl, i) => {
          const line = bl.lineNumber != null ? lineByNumber.get(bl.lineNumber) : undefined;
          const name = line ? [line.brandName, line.styleName].filter(Boolean).join(' ') : bl.sku;
          const variant = line ? [line.colorName, line.sizeName].filter(Boolean).join(' · ') : '';
          const others = boxes
            .filter((o) => o.boxNumber !== box.boxNumber)
            .flatMap((o) =>
              o.lines
                .filter(
                  (ol) => ol.lineNumber != null && ol.lineNumber === bl.lineNumber && ol.qty > 0,
                )
                .map((ol) => ({ box: o, qty: ol.qty, st: stateOf(o) })),
            );
          const editable = st === 'here' && bl.lineNumber != null && bl.qty > 0;
          const qty = qtyOf(box, bl);
          const missing = Math.max(0, bl.qty - qty);
          return (
            <li
              key={`${bl.lineNumber ?? i}-${bl.sku}`}
              className={cn(
                BOX_GRID,
                'flex flex-wrap items-center gap-x-3 gap-y-2 border-t px-3 py-2.5 text-sm first:border-t-0',
                missing > 0 && 'bg-amber-50 dark:bg-amber-950/30',
                st !== 'here' && 'text-muted-foreground',
              )}
            >
              <span className="hidden text-xs tabular-nums text-muted-foreground md:block">
                {bl.lineNumber ?? ''}
              </span>
              <div className="min-w-0 flex-1 md:flex-initial">
                <div className={cn('truncate font-medium', st === 'here' && 'text-foreground')}>
                  {name || '—'}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {variant && `${variant} · `}
                  <span className="font-mono">{bl.sku || '—'}</span>
                </div>
                {(others.length > 0 || line?.receivedQty != null) && (
                  <div className="text-xs text-muted-foreground">
                    {others.map((o, j) => (
                      <span key={o.box.boxNumber}>
                        {j > 0 && ' · '}
                        also {o.qty} in box {o.box.boxNumber}
                        {o.st === 'received' ? (
                          <span className="text-emerald-700 dark:text-emerald-400">
                            {' '}
                            · received
                          </span>
                        ) : o.st === 'out' ? (
                          <span className="text-amber-700 dark:text-amber-400"> · with UPS</span>
                        ) : null}
                      </span>
                    ))}
                    {line?.receivedQty != null && line.qtyShipped > 0 && (
                      <span>
                        {others.length > 0 && ' · '}
                        {line.receivedQty} of {line.qtyShipped} received so far
                      </span>
                    )}
                  </div>
                )}
                <div className="text-xs text-muted-foreground md:hidden">
                  {bl.lineNumber != null && `line ${bl.lineNumber} · `}
                  <span className={cn('font-medium', st === 'here' && 'text-foreground')}>
                    {bl.qty} in this box
                  </span>
                </div>
              </div>
              <span className="hidden text-right font-medium tabular-nums md:block">{bl.qty}</span>
              <div className="order-last flex w-full items-center gap-2 md:order-none md:w-auto md:justify-end">
                {editable && bl.lineNumber != null ? (
                  <>
                    <span className="text-xs text-muted-foreground md:hidden" aria-hidden="true">
                      Received
                    </span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={bl.qty}
                      value={qty}
                      onChange={(e) =>
                        bl.lineNumber != null &&
                        setQty(box.boxNumber, bl.lineNumber, e.target.value, bl.qty)
                      }
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label={`Received from box ${box.boxNumber}, line ${bl.lineNumber}`}
                      className={cn(
                        'h-11 flex-1 text-center text-base tabular-nums md:h-9 md:w-20 md:flex-initial md:text-right md:text-sm',
                        missing > 0 && 'border-amber-400 focus-visible:ring-amber-400',
                      )}
                    />
                    {missing > 0 && (
                      <span className="text-xs font-medium text-amber-700 md:hidden dark:text-amber-400">
                        −{missing} missing
                      </span>
                    )}
                  </>
                ) : (
                  <Badge
                    variant="outline"
                    className={cn(
                      st === 'received' &&
                        'border-emerald-300 text-emerald-700 dark:text-emerald-400',
                      st === 'out' && 'border-amber-300 text-amber-700 dark:text-amber-400',
                    )}
                  >
                    {st === 'received'
                      ? 'received'
                      : st === 'out'
                        ? 'with UPS'
                        : st === 'undone'
                          ? 'undone'
                          : `${bl.qty}`}
                  </Badge>
                )}
              </div>
              <span className="hidden text-right tabular-nums md:block">
                {editable && missing > 0 ? (
                  <span className="font-medium text-amber-700 dark:text-amber-400">−{missing}</span>
                ) : editable ? (
                  <span className="text-muted-foreground">✓</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ───────────────────────────── whole-invoice receiving (the original flow) ─────────────────────────────

/** S&S prefixes most statuses with "Shipped - "; the rest of the string is what the operator needs. */
export function statusText(raw: string | undefined, state: DeliveryState | undefined): string {
  const trimmed = raw?.replace(/^shipped\s*-\s*/i, '').trim();
  if (trimmed) return trimmed;
  return state === 'delivered'
    ? 'Delivered'
    : state === 'partially_delivered'
      ? 'Partly delivered'
      : 'In transit';
}

export function DeliveryBadge({ state, raw }: { state: DeliveryState; raw?: string }) {
  const label = statusText(raw, state);
  if (state === 'delivered') {
    return (
      <Badge
        variant="outline"
        className="border-emerald-300 text-emerald-700 dark:text-emerald-400"
      >
        {label}
      </Badge>
    );
  }
  if (state === 'partially_delivered') {
    return (
      <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-blue-300 text-blue-700 dark:text-blue-300">
      <Truck className="size-3" /> {label}
    </Badge>
  );
}

/** Where each invoice line was packed: lineNumber → [{box, qty}], in box order. */
function boxesByLine(
  boxes: readonly ReceivingBox[],
): Map<number, { box: ReceivingBox; qty: number }[]> {
  const out = new Map<number, { box: ReceivingBox; qty: number }[]>();
  for (const box of boxes) {
    for (const l of box.lines) {
      if (l.lineNumber == null || l.qty <= 0) continue;
      const list = out.get(l.lineNumber) ?? [];
      list.push({ box, qty: l.qty });
      out.set(l.lineNumber, list);
    }
  }
  return out;
}

/**
 * Whole-invoice receiving — today's flow, kept for orders without a usable per-box manifest and for
 * invoices already received as a whole: every line at once, Received prefilled at shipped, the
 * "not every box delivered" gate when S&S says a box is still out.
 */
function WholeShipment({ result }: { result: ReceivingResult }) {
  const short = result.totals.shortLineCount > 0;
  const boxes = result.boxes ?? [];
  const placement = useMemo(() => boxesByLine(boxes), [boxes]);
  // M2: S&S does not say every box is here. Unknown (no status at all) says nothing — a guess either
  // way would be noise or a false all-clear.
  const notAllHere =
    result.deliveryState === 'in_transit' || result.deliveryState === 'partially_delivered';
  const orderDate = fmtDate(result.orderDate);
  const shipDate = fmtDate(result.shipDate);

  // Operator-entered actual-arrived per lineNumber. Absent = trust S&S's shipped qty. A line without a
  // lineNumber can't be overridden (it's the RECEIVE idempotency key), so it's display-only.
  const [received, setReceived] = useState<Record<number, number>>({});
  const [note, setNote] = useState('');

  const setReceivedFor = useCallback((lineNumber: number, raw: string, shipped: number) => {
    const parsed = raw === '' ? 0 : Math.trunc(Number(raw));
    const clamped = Number.isFinite(parsed) ? Math.min(Math.max(0, parsed), shipped) : 0;
    setReceived((prev) => ({ ...prev, [lineNumber]: clamped }));
  }, []);

  // Lines the operator marked short on arrival (received < shipped) → the shortfall summary.
  const arrivalShort = useMemo(() => {
    let lineCount = 0;
    let pieces = 0;
    for (const l of result.lines) {
      if (l.lineNumber == null) continue;
      const r = received[l.lineNumber];
      if (r != null && r < l.qtyShipped) {
        lineCount += 1;
        pieces += l.qtyShipped - r;
      }
    }
    return { lineCount, pieces };
  }, [result.lines, received]);

  // Only lines whose received differs from S&S's shipped become overrides (server clamps + validates).
  const overrides = useMemo<LineOverride[]>(() => {
    const trimmed = note.trim();
    const out: LineOverride[] = [];
    for (const l of result.lines) {
      if (l.lineNumber == null) continue;
      const r = received[l.lineNumber];
      if (r == null || r === l.qtyShipped) continue;
      out.push({ lineNumber: l.lineNumber, receivedQty: r, note: trimmed || undefined });
    }
    return out;
  }, [result.lines, received, note]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold">Invoice {result.invoiceNumber}</span>
              {result.orderNumber && (
                <span className="text-sm text-muted-foreground">order {result.orderNumber}</span>
              )}
              {result.cartonRecognized && (
                <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                  <PackageCheck className="size-3" /> Carton matched
                </Badge>
              )}
              {result.deliveryState && (
                <DeliveryBadge
                  state={result.deliveryState}
                  raw={boxes.length > 0 ? undefined : result.deliveryStatus}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-muted-foreground">
              {result.poNumber && <span>PO {result.poNumber}</span>}
              {orderDate && <span>Ordered {orderDate}</span>}
              {shipDate && <span>Shipped {shipDate}</span>}
              {result.shippingCarrier && <span>{result.shippingCarrier}</span>}
              {result.trackingNumber && (
                <span className="font-mono text-xs">{result.trackingNumber}</span>
              )}
              {result.warehouseAbbr && <span>from {result.warehouseAbbr}</span>}
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="font-medium tabular-nums">
              {result.totals.totalShipped}/{result.totals.totalOrdered} pieces
            </div>
            <div className="text-muted-foreground">
              {result.totals.lineCount} lines
              {result.totalBoxes != null && ` · ${result.totalBoxes} boxes`}
            </div>
          </div>
        </div>
      </div>

      {short ? (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>S&amp;S shipped short</AlertTitle>
          <AlertDescription>
            S&amp;S shipped {result.totals.shortPieceCount}{' '}
            {result.totals.shortPieceCount === 1 ? 'piece' : 'pieces'} short across{' '}
            {result.totals.shortLineCount} of {result.totals.lineCount} lines. Check the highlighted
            rows against the box before confirming.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-emerald-300 text-emerald-800 dark:text-emerald-300">
          <CheckCircle2 className="size-4" />
          <AlertTitle>Shipped complete</AlertTitle>
          <AlertDescription>
            Every line shipped in full — {result.totals.totalShipped} pieces across{' '}
            {result.totals.lineCount} lines.
          </AlertDescription>
        </Alert>
      )}

      {result.receipt?.status === 'complete' && (
        <Alert className="border-emerald-300 text-emerald-800 dark:text-emerald-300">
          <CheckCircle2 className="size-4" />
          <AlertTitle>Already received</AlertTitle>
          <AlertDescription>
            This invoice was received {fmtWhen(result.receipt.lastReceivedAt) ?? 'earlier'}
            {result.receipt.receivedBy ? ` by ${result.receipt.receivedBy}` : ''}. Confirming again
            changes nothing.
          </AlertDescription>
        </Alert>
      )}

      {notAllHere && <NotAllHereAlert result={result} />}

      {boxes.length > 1 && <ReceivingBoxes boxes={boxes} lines={result.lines} />}

      <ReceivingLines
        lines={result.lines}
        received={received}
        onReceivedChange={setReceivedFor}
        placement={boxes.length > 1 ? placement : undefined}
      />

      {/* The confirm CTA sits BELOW the table it depends on (P3, F5-5): scan → check/edit the lines
          top-to-bottom → confirm, with no scroll-back-up. */}
      <ConfirmReceived
        code={result.scannedCode}
        overrides={overrides}
        arrivalShort={arrivalShort}
        note={note}
        setNote={setNote}
        requireArrivalCheck={notAllHere}
      />
    </div>
  );
}

/**
 * M2 — the carrier has not delivered every box, per S&S. Confirming records every line as physically
 * here, so this names the boxes still out (with their own tracking links) before anyone does that.
 */
function NotAllHereAlert({ result }: { result: ReceivingResult }) {
  const boxes = result.boxes ?? [];
  const out = boxes.filter((b) => b.deliveryState != null && b.deliveryState !== 'delivered');
  const partial = result.deliveryState === 'partially_delivered';
  return (
    <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <Truck className="size-4" />
      <AlertTitle>
        {partial
          ? 'Not every box has been delivered yet'
          : 'S&S doesn’t show this shipment as delivered yet'}
      </AlertTitle>
      <AlertDescription className="space-y-1.5 text-amber-900 dark:text-amber-200">
        {out.length > 0 ? (
          <ul className="space-y-0.5">
            {out.map((b) => (
              <li key={b.boxNumber} className="flex flex-wrap items-center gap-x-2">
                <span className="font-medium">
                  Box {b.boxNumber} of {boxes.length}
                </span>
                <span>· {statusText(b.deliveryStatus, b.deliveryState)}</span>
                {b.contentsKnown && <span>· {b.pieces} pieces</span>}
                {b.trackingNumber && <TrackingLink box={b} />}
              </li>
            ))}
          </ul>
        ) : (
          <p>S&amp;S status: {statusText(result.deliveryStatus, result.deliveryState)}.</p>
        )}
        <p>
          Confirming records every line as here. If a box hasn’t arrived, the simplest path is to
          wait and receive the whole invoice once it’s in. If you receive now, lower that box’s
          lines — they’ll sit under “S&amp;S owes us” until you mark them <em>All arrived</em>.
        </p>
      </AlertDescription>
    </Alert>
  );
}

export function TrackingLink({ box }: { box: ReceivingBox }) {
  if (!box.trackingNumber) return null;
  return box.trackingUrl ? (
    <a
      href={box.trackingUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-full items-center gap-1 font-mono text-xs underline underline-offset-2 hover:no-underline"
    >
      <span className="min-w-0 truncate">{box.trackingNumber}</span>
      <ExternalLink className="size-3 shrink-0" aria-hidden />
    </a>
  ) : (
    <span className="font-mono text-xs">{box.trackingNumber}</span>
  );
}

/**
 * M3 — the shipment box by box, as S&S packed it: what is in each carton, its weight, and its own
 * carrier tracking + delivery status. Read-only context; the Received counts below are still per
 * invoice line. The scanned carton opens by default so its contents are the first thing on screen.
 */
function ReceivingBoxes({ boxes, lines }: { boxes: ReceivingBox[]; lines: ReceivingLine[] }) {
  const byNumber = useMemo(() => {
    const m = new Map<number, ReceivingLine>();
    for (const l of lines) if (l.lineNumber != null) m.set(l.lineNumber, l);
    return m;
  }, [lines]);
  const [open, setOpen] = useState<Set<number>>(
    () => new Set(boxes.filter((b) => b.scanned).map((b) => b.boxNumber)),
  );
  const toggle = (n: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  const known = boxes.filter((b) => b.deliveryState != null);
  const delivered = known.filter((b) => b.deliveryState === 'delivered').length;

  return (
    <section className="overflow-hidden rounded-lg border" aria-label="Boxes in this shipment">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Package className="size-3.5" aria-hidden /> {boxes.length} boxes
        </span>
        {known.length === boxes.length && (
          <span>
            {delivered} of {boxes.length} delivered
          </span>
        )}
      </div>
      <ul>
        {boxes.map((b) => {
          const isOpen = open.has(b.boxNumber);
          return (
            <li
              key={b.boxNumber}
              className={cn('border-t', b.scanned && 'bg-emerald-50/60 dark:bg-emerald-950/20')}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 text-sm">
                <button
                  type="button"
                  onClick={() => toggle(b.boxNumber)}
                  aria-expanded={isOpen}
                  className="flex min-w-0 basis-full items-center gap-2 text-left sm:basis-auto sm:flex-1"
                >
                  {isOpen ? (
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  ) : (
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <span className="shrink-0 whitespace-nowrap font-medium">
                    Box {b.boxNumber} of {boxes.length}
                  </span>
                  <span className="min-w-0 text-xs text-muted-foreground">
                    {b.contentsKnown
                      ? `${b.pieces} ${b.pieces === 1 ? 'piece' : 'pieces'} · ${b.lines.length} ${b.lines.length === 1 ? 'line' : 'lines'}`
                      : 'contents not listed by S&S'}
                    {b.weightLb != null && ` · ${b.weightLb} lb`}
                  </span>
                </button>
                {/* Phone: its own row, indented under the chevron — sharing the title's row squeezed
                    "Box 2 of 2" onto four lines at 390px. */}
                <span className="flex flex-wrap items-center gap-1.5 pl-6 sm:pl-0">
                  {b.scanned && (
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                      <ScanLine className="size-3" /> Scanned
                    </Badge>
                  )}
                  {b.deliveryState && (
                    <DeliveryBadge state={b.deliveryState} raw={b.deliveryStatus} />
                  )}
                  <TrackingLink box={b} />
                </span>
              </div>
              {isOpen && b.contentsKnown && (
                <ul className="border-t bg-background px-3 py-1.5 text-sm">
                  {b.lines.map((bl) => {
                    const line = bl.lineNumber != null ? byNumber.get(bl.lineNumber) : undefined;
                    const name = line
                      ? [line.brandName, line.styleName].filter(Boolean).join(' ')
                      : bl.sku;
                    const variant = line
                      ? [line.colorName, line.sizeName].filter(Boolean).join(' · ')
                      : '';
                    return (
                      <li
                        key={`${bl.lineNumber ?? ''}-${bl.sku}-${bl.qty}`}
                        className="flex items-baseline justify-between gap-3 py-1"
                      >
                        <span className="min-w-0 truncate">
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {bl.lineNumber != null ? `#${bl.lineNumber} ` : ''}
                          </span>
                          {name || '—'}
                          {variant && (
                            <span className="text-xs text-muted-foreground"> · {variant}</span>
                          )}
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">{bl.qty}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* Desktop line grid: # | item | ordered | shipped | received | missing. The old 9-column, 760px
   table split the item across four cells (sku / brand+style / colour / size) — the width that pushed
   the ONE editable cell, Received, to column 8 of 9 and off the side of a phone. */
const LINE_GRID =
  'md:grid md:grid-cols-[2.5rem_minmax(0,1fr)_4.5rem_4.5rem_7rem_5rem] md:items-center md:gap-x-2';

/**
 * The scanned shipment's lines. One DOM, two layouts (same shape as the purchase draft): columns from
 * `md` up, and on a phone a card per line whose Received field is full-width and thumb-height on its
 * own row — this is the screen someone uses standing at an open box, one-handed.
 */
function ReceivingLines({
  lines,
  received,
  onReceivedChange,
  placement,
}: {
  lines: ReceivingLine[];
  received: Record<number, number>;
  onReceivedChange: (lineNumber: number, raw: string, shipped: number) => void;
  /** Multi-box shipments only: which box(es) each line was packed in. */
  placement?: Map<number, { box: ReceivingBox; qty: number }[]>;
}) {
  return (
    <section className="overflow-hidden rounded-lg border">
      <div
        className={cn(
          LINE_GRID,
          'hidden bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground md:grid',
        )}
      >
        <span>#</span>
        <span>Item</span>
        <span className="text-right">Ordered</span>
        <span className="text-right">Shipped</span>
        <span className="text-right">Received</span>
        <span className="text-right">Missing</span>
      </div>
      <ul>
        {lines.map((line, i) => (
          <ReceivingLineRow
            key={line.lineNumber ?? `${line.sku}-${i}`}
            line={line}
            fallbackNumber={i + 1}
            received={received}
            onReceivedChange={onReceivedChange}
            inBoxes={line.lineNumber != null ? placement?.get(line.lineNumber) : undefined}
          />
        ))}
      </ul>
    </section>
  );
}

function ReceivingLineRow({
  line,
  fallbackNumber,
  received,
  onReceivedChange,
  inBoxes,
}: {
  line: ReceivingLine;
  fallbackNumber: number;
  received: Record<number, number>;
  onReceivedChange: (lineNumber: number, raw: string, shipped: number) => void;
  inBoxes?: { box: ReceivingBox; qty: number }[];
}) {
  const ln = line.lineNumber;
  // A line with no lineNumber can't be overridden — that number IS the RECEIVE idempotency key.
  const canEdit = ln != null;
  const rec = canEdit ? (received[ln] ?? line.qtyShipped) : line.qtyShipped;
  const missing = Math.max(0, line.qtyShipped - rec);
  const arrivalShort = missing > 0;
  const shown = ln ?? fallbackNumber;
  const name = [line.brandName, line.styleName].filter(Boolean).join(' ') || '—';
  const variant = [line.colorName, line.sizeName].filter(Boolean).join(' · ');

  return (
    <li
      className={cn(
        LINE_GRID,
        'flex flex-wrap items-center gap-x-3 gap-y-2 border-t px-3 py-2.5 text-sm',
        arrivalShort
          ? 'bg-amber-50 dark:bg-amber-950/30'
          : line.shortShipped
            ? 'bg-red-50 dark:bg-red-950/30'
            : line.overShipped
              ? 'bg-blue-50 dark:bg-blue-950/30'
              : 'odd:bg-muted/20',
      )}
    >
      <span className="hidden text-xs tabular-nums text-muted-foreground md:block">{shown}</span>

      <div className="min-w-0 flex-1 md:flex-initial">
        <div className="truncate font-medium">{name}</div>
        {line.title && <div className="truncate text-xs text-muted-foreground">{line.title}</div>}
        <div className="truncate text-xs text-muted-foreground">
          {variant && `${variant} · `}
          <span className="font-mono">{line.sku || '—'}</span>
        </div>
        {inBoxes && inBoxes.length > 0 && (
          <div className="text-xs text-muted-foreground">
            {inBoxes.map(({ box, qty }, i) => (
              <span key={box.boxNumber}>
                {i > 0 && ' · '}
                <span className={cn(box.scanned && 'font-medium text-foreground')}>
                  box {box.boxNumber}: {qty}
                </span>
                {box.deliveryState && box.deliveryState !== 'delivered' && (
                  <span className="text-amber-700 dark:text-amber-400"> (not delivered)</span>
                )}
              </span>
            ))}
          </div>
        )}
        {/* Phone: the hidden columns, said in one line. "shipped" is what you count against. */}
        <div className="text-xs text-muted-foreground md:hidden">
          line {shown} · {line.qtyOrdered} ordered ·{' '}
          <span className="font-medium text-foreground">{line.qtyShipped} shipped</span>
        </div>
      </div>

      <span className="hidden text-right tabular-nums text-muted-foreground md:block">
        {line.qtyOrdered}
      </span>
      <span className="hidden text-right font-medium tabular-nums md:block">{line.qtyShipped}</span>

      {/* Received — full width on its own row on a phone, one grid cell on desktop. */}
      <div className="order-last flex w-full items-center gap-2 md:order-none md:w-auto md:justify-end">
        <span className="text-xs text-muted-foreground md:hidden" aria-hidden="true">
          Received
        </span>
        {canEdit && line.qtyShipped > 0 ? (
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={line.qtyShipped}
            value={rec}
            onChange={(e) => onReceivedChange(ln, e.target.value, line.qtyShipped)}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={`Received quantity for line ${ln}`}
            className={cn(
              'h-11 flex-1 text-center text-base tabular-nums md:h-9 md:w-20 md:flex-initial md:text-right md:text-sm',
              arrivalShort && 'border-amber-400 focus-visible:ring-amber-400',
            )}
          />
        ) : (
          <span className="flex-1 text-center text-muted-foreground md:flex-initial md:text-right">
            {line.qtyShipped}
          </span>
        )}
        {arrivalShort && (
          <span className="text-xs font-medium text-amber-700 md:hidden dark:text-amber-400">
            −{missing} missing
          </span>
        )}
      </div>

      <span className="hidden text-right tabular-nums md:block">
        {arrivalShort ? (
          <span className="font-medium text-amber-700 dark:text-amber-400">−{missing}</span>
        ) : (
          <span className="text-muted-foreground">✓</span>
        )}
      </span>
    </li>
  );
}

function ConfirmReceived({
  code,
  overrides,
  arrivalShort,
  note,
  setNote,
  requireArrivalCheck,
}: {
  code: string;
  overrides: LineOverride[];
  arrivalShort: { lineCount: number; pieces: number };
  note: string;
  setNote: (v: string) => void;
  /** S&S doesn't show every box delivered → the operator must tick that they counted what's here. */
  requireArrivalCheck: boolean;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  // Cosmetic gate mirroring the server's requireInventoryWrite; the server 403 is the real one.
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  // The blank whose drawer is open (drill-in from the on-hand list); null = closed.
  const [selected, setSelected] = useState<BlankOnHand | null>(null);
  const [askReverse, setAskReverse] = useState(false);
  const [arrivalChecked, setArrivalChecked] = useState(false);
  const blockedOnArrival = requireArrivalCheck && !arrivalChecked;

  const confirm = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/receiving/confirm', {
        body: { code, lineOverrides: overrides.length > 0 ? overrides : undefined },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Confirm failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    // Invalidate the WHOLE inventory family (list + overview + draft) so the hub KPI strip, tab
    // badges, and reorder-$ KPI mounted above this tab refresh — plus the open-shortages worklist.
    onSuccess: () => {
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
    },
  });

  const reverse = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/receiving/reverse', { body: { code } });
      if (error) throw new Error(error?.error?.detail ?? 'Reverse failed.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      setAskReverse(false);
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: SHORTAGES_KEY });
    },
  });

  const done = confirm.data;
  const undone = reverse.data;
  // Surface the on-hand the API returns (previously discarded, gap #12): post-reversal once reversed,
  // else post-receipt. Each blank drills into its shared drawer.
  const onHandList = undone?.onHand ?? done?.onHand ?? [];
  const recordedShortPieces = done?.shortages.reduce((sum, s) => sum + s.shortQty, 0) ?? 0;

  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      {/* Shortage note — shown once the operator has lowered any Received count. Attached to every
          shorted line's claim record. */}
      {arrivalShort.lineCount > 0 && !done && (
        <div className="space-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/40">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {arrivalShort.pieces} {arrivalShort.pieces === 1 ? 'piece' : 'pieces'} missing from the
            box across {arrivalShort.lineCount} {arrivalShort.lineCount === 1 ? 'line' : 'lines'}.
            Only what arrived goes into stock — the rest becomes an S&amp;S claim below.
          </p>
          <div className="space-y-1">
            <Label htmlFor="shortage-note" className="text-xs text-amber-800 dark:text-amber-300">
              Note for the S&amp;S claim (optional)
            </Label>
            <Input
              id="shortage-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={280}
              placeholder="e.g. 2XL not in the box — S&S packing error"
              className="bg-background"
            />
          </div>
        </div>
      )}

      {requireArrivalCheck && !done && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 px-3 py-2 dark:border-amber-900">
          <Checkbox
            id="arrival-check"
            checked={arrivalChecked}
            onCheckedChange={(v) => setArrivalChecked(v === true)}
            className="mt-0.5"
          />
          <Label htmlFor="arrival-check" className="text-sm font-normal leading-snug">
            I checked the boxes in front of me — the Received counts above are only what’s
            physically here.
          </Label>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => confirm.mutate()}
          disabled={!canWrite || confirm.isPending || !!done || blockedOnArrival}
          title={canWrite ? undefined : 'Admins only'}
        >
          {confirm.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <PackageCheck className="size-4" />
          )}
          {arrivalShort.lineCount > 0 ? 'Confirm what arrived' : 'Confirm received'}
        </Button>
        {done && !undone && (
          <span className="text-sm text-emerald-700 dark:text-emerald-400">
            {done.receivedLineCount > 0
              ? `Recorded — ${done.receivedPieces} ${done.receivedPieces === 1 ? 'piece' : 'pieces'} across ${done.receivedLineCount} ${done.receivedLineCount === 1 ? 'line' : 'lines'} added to on-hand.`
              : done.shortages.length > 0
                ? 'Nothing added — every line was recorded short.'
                : 'Already received — inventory unchanged.'}
            {done.alreadyReceivedLineCount > 0 && done.receivedLineCount > 0
              ? ` (${done.alreadyReceivedLineCount} already on file.)`
              : ''}
          </span>
        )}
        {confirm.isError && (
          <span className="text-sm text-destructive">
            {confirm.error instanceof Error ? confirm.error.message : 'Confirm failed.'}
          </span>
        )}
        {canWrite && !done && !confirm.isError && arrivalShort.lineCount === 0 && (
          <span className="text-sm text-muted-foreground">Adds every line to stock.</span>
        )}
        {!canWrite && (
          <span className="text-sm text-muted-foreground">
            Read-only — ask an admin to record the receipt.
          </span>
        )}
      </div>

      {/* Post-confirm shortage recap — what was logged as owed by S&S for the claim. */}
      {done && done.shortages.length > 0 && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          Logged {done.shortages.length} S&amp;S {done.shortages.length === 1 ? 'claim' : 'claims'}{' '}
          — {recordedShortPieces} {recordedShortPieces === 1 ? 'piece' : 'pieces'} missing. See the
          claims list below.
        </p>
      )}

      {/* Receive-against-PO tie-back (Phase 3c): the confirm/reverse auto-applied this receipt to the
          matching FSP purchase order — surface it so the operator sees the PO advanced with no manual step. */}
      {(() => {
        const tie = undone?.purchaseOrder ?? done?.purchaseOrder;
        if (!tie) return null;
        const verb = undone ? 'Un-applied from' : 'Applied to';
        // Key the state on the PO's actual status (not fullyReceived): a CLOSED PO is done regardless of
        // whether every line reached its ordered qty (a manual close can be CLOSED-but-partial).
        const state =
          tie.status === 'CLOSED'
            ? 'now received (closed)'
            : tie.status === 'ORDERED'
              ? 'back to on-order'
              : 'now partially received';
        return (
          <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            {verb} purchase order <span className="font-mono font-medium">{tie.poNumber}</span> —{' '}
            {state}.
          </p>
        );
      })()}

      {onHandList.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {undone ? 'On hand after reversal' : 'On hand after this receipt'}
          </p>
          <ul className="divide-y rounded-md border text-sm">
            {onHandList.map((b) => (
              <li key={b.ssSku}>
                <button
                  type="button"
                  onClick={() => setSelected(b)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left hover:bg-muted/50"
                >
                  <span className="min-w-0 truncate">
                    <span className="text-primary underline-offset-2 hover:underline">
                      {blankLabel(b)}
                    </span>
                    {variantLabel(b) && (
                      <span className="text-muted-foreground"> · {variantLabel(b)}</span>
                    )}
                  </span>
                  <span
                    className={
                      b.onHand < 0
                        ? 'shrink-0 tabular-nums text-red-700 dark:text-red-400'
                        : 'shrink-0 tabular-nums'
                    }
                  >
                    {b.onHand}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Undo a receipt confirmed in error — a compensating entry (the receipt log is kept). Available
          once a receipt has been confirmed this session; the server gate + 403 is the real guard. */}
      {done && !undone && (
        <div className="space-y-2">
          {askReverse ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-900 dark:bg-amber-950/40">
              <span className="text-amber-800 dark:text-amber-300">
                Undo this receipt? Removes the received pieces from stock and clears its claims.
                History is kept.
              </span>
              <div className="ml-auto flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAskReverse(false)}
                  disabled={reverse.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => reverse.mutate()}
                  disabled={reverse.isPending}
                >
                  {reverse.isPending && <Loader2 className="size-4 animate-spin" />}
                  Yes, reverse
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAskReverse(true)}
              disabled={!canWrite || reverse.isPending}
              title={
                canWrite
                  ? undefined
                  : 'Reversing a receipt requires the admin or inventory-admin group'
              }
            >
              <Undo2 className="size-4" />
              Reverse this receipt
            </Button>
          )}
          {reverse.isError && (
            <span className="text-sm text-destructive">
              {reverse.error instanceof Error ? reverse.error.message : 'Reverse failed.'}
            </span>
          )}
        </div>
      )}

      {undone && (
        <span className="text-sm text-amber-700 dark:text-amber-400">
          {undone.reversedLineCount > 0
            ? `Reversed — ${undone.reversedPieces} ${undone.reversedPieces === 1 ? 'piece' : 'pieces'} removed across ${undone.reversedLineCount} ${undone.reversedLineCount === 1 ? 'line' : 'lines'}. The receipt log is preserved.`
            : 'Nothing to reverse — this receipt had no recorded lines.'}
          {undone.alreadyReversedLineCount > 0
            ? ` (${undone.alreadyReversedLineCount} already reversed.)`
            : ''}
        </span>
      )}

      <BlankDetailDialog blank={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
